(() => {
  if (customElements.get('younger-returns')) return;
  const MIN_PHOTOS = 2, MAX_PHOTOS = 5, MAX_SIDE = 1600;

  const shrink = (file) => new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const scale = Math.min(1, MAX_SIDE / Math.max(img.width, img.height));
      const c = document.createElement('canvas');
      c.width = Math.round(img.width * scale);
      c.height = Math.round(img.height * scale);
      c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
      URL.revokeObjectURL(url);
      c.toBlob((b) => resolve(b || file), 'image/jpeg', 0.82);
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
    img.src = url;
  });

  class YoungerReturns extends HTMLElement {
    connectedCallback() {
      this.lookupUrl = this.dataset.lookupUrl;
      this.submitUrl = this.dataset.submitUrl;
      this.order = null;
      this.items = [];
      this.$ = (s) => this.querySelector(s);
      this.$('[data-step="1"]').addEventListener('submit', (e) => { e.preventDefault(); this.lookup(); });
      this.$('[data-step="2"]').addEventListener('submit', (e) => { e.preventDefault(); this.submit(); });
      this.$('[data-back]').addEventListener('click', () => this.show(1));
    }

    show(n) {
      this.querySelectorAll('[data-step]').forEach((el) => { el.hidden = el.dataset.step !== String(n); });
      this.$('[data-progress]').style.width = ({ 1: '25%', 2: '65%', 3: '100%' })[n];
      this.error('');
      this.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    error(msg) {
      const el = this.$('[data-error]');
      el.textContent = msg;
      el.classList.toggle('is-on', !!msg);
    }

    busy(form, on, label) {
      const btn = form.querySelector('button[type="submit"]');
      if (!btn.dataset.label) btn.dataset.label = btn.textContent;
      btn.disabled = on;
      btn.textContent = on ? label : btn.dataset.label;
    }

    async lookup() {
      const form = this.$('[data-step="1"]');
      const order = form.order.value.trim().replace(/^#?/, '#');
      const contact = form.contact.value.trim();
      if (order.length < 2 || !contact) return this.error('Please enter your order number and the phone or email you used.');
      if (!this.lookupUrl) return this.error('Returns are not available right now. Please contact us.');
      this.busy(form, true, 'Finding your order…');
      try {
        const res = await fetch(this.lookupUrl, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ order, contact }) });
        const data = await res.json();
        if (!data.ok) throw new Error(data.error || "We couldn't find that order. Check the number and phone or email.");
        this.order = data.order;
        this.renderItems(data.items || []);
        this.show(2);
      } catch (err) {
        this.error(err.message || 'Something went wrong. Please try again.');
      } finally {
        this.busy(form, false);
      }
    }

    renderItems(items) {
      const o = this.order;
      this.$('[data-order-meta]').innerHTML = '';
      const meta = this.$('[data-order-meta]');
      meta.append(`Order ${o.name}${o.deliveredAt ? ' · Delivered ' + new Date(o.deliveredAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : ''} · `);
      const b = document.createElement('b');
      b.textContent = `${o.daysLeft} day${o.daysLeft === 1 ? '' : 's'} left`;
      meta.append(b);

      const wrap = this.$('[data-items]');
      wrap.innerHTML = '';
      const tpl = this.$('[data-item-tpl]');
      this.items = items.map((it) => {
        const node = tpl.content.firstElementChild.cloneNode(true);
        const state = { ...it, el: node, selected: false, type: 'EXCHANGE', photos: [] };
        node.querySelector('.yr__item-img').src = it.image || '';
        node.querySelector('.yr__item-title').textContent = it.title;
        node.querySelector('.yr__item-sub').textContent = [it.variantTitle, it.price].filter(Boolean).join(' · ');
        node.querySelector('[data-select]').addEventListener('change', (e) => {
          state.selected = e.target.checked;
          node.classList.toggle('is-on', state.selected);
        });

        const variantSel = node.querySelector('[data-variant]');
        const opts = (it.options || []).filter((v) => v.variantId !== it.variantId);
        opts.forEach((v) => {
          const op = document.createElement('option');
          op.value = v.variantId;
          op.textContent = v.available ? v.title : `${v.title} — sold out`;
          op.disabled = !v.available;
          variantSel.append(op);
        });
        const firstAvail = opts.find((v) => v.available);
        if (firstAvail) variantSel.value = firstAvail.variantId;
        const canExchange = !!firstAvail;

        const qtySel = node.querySelector('[data-qty]');
        for (let q = 1; q <= (it.quantity || 1); q++) qtySel.add(new Option(String(q), String(q)));
        node.querySelector('[data-qty-field]').hidden = (it.quantity || 1) < 2;

        const segBtns = node.querySelectorAll('.yr__seg button');
        const setType = (t) => {
          state.type = t;
          segBtns.forEach((btn) => btn.setAttribute('aria-pressed', String(btn.dataset.type === t)));
          node.querySelector('[data-exchange-field]').hidden = t !== 'EXCHANGE';
          node.querySelector('[data-why]').textContent = t === 'EXCHANGE' ? 'exchanging' : 'returning';
        };
        segBtns.forEach((btn) => btn.addEventListener('click', () => setType(btn.dataset.type)));
        if (!canExchange) {
          node.querySelector('[data-type="EXCHANGE"]').disabled = true;
          setType('REFUND');
        }

        node.querySelector('[data-file]').addEventListener('change', async (e) => {
          const files = [...e.target.files].slice(0, MAX_PHOTOS - state.photos.length);
          e.target.value = '';
          for (const f of files) state.photos.push(await shrink(f));
          this.drawPhotos(state);
        });
        this.drawPhotos(state);
        wrap.append(node);
        return state;
      });
      if (!this.items.length) wrap.textContent = 'There are no items on this order that can be returned.';
    }

    drawPhotos(state) {
      const grid = state.el.querySelector('[data-photos]');
      const add = grid.querySelector('.yr__add');
      grid.querySelectorAll('.yr__thumb').forEach((t) => t.remove());
      state.photos.forEach((blob, i) => {
        const t = document.createElement('div');
        t.className = 'yr__thumb';
        const img = document.createElement('img');
        img.src = URL.createObjectURL(blob);
        img.alt = `Photo ${i + 1}`;
        const del = document.createElement('button');
        del.type = 'button';
        del.textContent = '×';
        del.setAttribute('aria-label', `Remove photo ${i + 1}`);
        del.addEventListener('click', () => { state.photos.splice(i, 1); this.drawPhotos(state); });
        t.append(img, del);
        grid.insertBefore(t, add);
      });
      add.hidden = state.photos.length >= MAX_PHOTOS;
      state.el.querySelector('[data-photo-count]').textContent = `${state.photos.length} of ${MAX_PHOTOS} photos · at least ${MIN_PHOTOS} needed`;
    }

    async submit() {
      const form = this.$('[data-step="2"]');
      const chosen = this.items.filter((i) => i.selected);
      if (!chosen.length) return this.error('Select at least one item.');
      for (const it of chosen) {
        it.reason = it.el.querySelector('[data-reason]').value.trim();
        if (it.reason.length < 3) return this.error(`Tell us why you're returning "${it.title}".`);
        if (it.photos.length < MIN_PHOTOS) return this.error(`Add at least ${MIN_PHOTOS} photos for "${it.title}", with the tag showing.`);
      }
      if (!form.confirm.checked) return this.error('Please confirm the items are unworn with the tag still on.');
      if (!this.submitUrl) return this.error('Returns are not available right now. Please contact us.');

      const payload = {
        orderId: this.order.id,
        orderName: this.order.name,
        contact: this.$('#yr-contact').value.trim(),
        token: this.order.token,
        items: chosen.map((it) => ({
          lineItemId: it.lineItemId,
          fulfillmentLineItemId: it.fulfillmentLineItemId,
          title: it.title,
          variantTitle: it.variantTitle,
          quantity: Number(it.el.querySelector('[data-qty]').value || 1),
          type: it.type,
          exchangeVariantId: it.type === 'EXCHANGE' ? it.el.querySelector('[data-variant]').value : null,
          exchangeVariantTitle: it.type === 'EXCHANGE' ? it.el.querySelector('[data-variant]').selectedOptions[0]?.textContent : null,
          reason: it.reason,
        })),
      };
      const fd = new FormData();
      fd.append('payload', JSON.stringify(payload));
      chosen.forEach((it) => {
        const key = 'photos_' + String(it.lineItemId).split('/').pop();
        it.photos.forEach((b, n) => fd.append(`${key}_${n + 1}`, b, `${key}_${n + 1}.jpg`));
      });

      this.busy(form, true, 'Sending…');
      try {
        const res = await fetch(this.submitUrl, { method: 'POST', body: fd });
        const data = await res.json();
        if (!data.ok) throw new Error(data.error || 'We could not send your request. Please try again.');
        const sum = this.$('[data-summary]');
        sum.innerHTML = '';
        const line = (k, v) => { const d = document.createElement('div'); const s = document.createElement('b'); s.textContent = k + ': '; d.append(s, v); sum.append(d); };
        line('Request', data.requestId);
        line('Order', this.order.name);
        payload.items.forEach((it) => line(it.type === 'EXCHANGE' ? 'Exchange' : 'Refund', it.type === 'EXCHANGE' ? `${it.title} · ${it.variantTitle} → ${it.exchangeVariantTitle}` : `${it.title} · ${it.variantTitle}`));
        this.show(3);
      } catch (err) {
        this.error(err.message || 'Something went wrong. Please try again.');
      } finally {
        this.busy(form, false);
      }
    }
  }
  customElements.define('younger-returns', YoungerReturns);
})();
